import { useParams, Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';
import { worksData } from '../../mocks/works';

export default function WorkDetailPage() {
  const { id } = useParams<{ id: string }>();

  const currentIndex = worksData.findIndex(work => work.id === id);
  const work = worksData[currentIndex];

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  if (!work) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-500">作品が見つかりません</p>
      </div>
    );
  }

  const prevWork = currentIndex > 0 ? worksData[currentIndex - 1] : null;
  const nextWork = currentIndex < worksData.length - 1 ? worksData[currentIndex + 1] : null;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: work.title,
    url: `${siteUrl}/works/${work.id}`,
    dateCreated: work.year,
    description: work.description,
    inLanguage: 'ja',
    creator: {
      '@type': 'Person',
      name: 'EIJI HOSHINO',
      url: `${siteUrl}/about`,
    },
    genre: work.category,
    thumbnailUrl: work.thumbnail,
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title={`${work.title} | New Age Cinema - EIJI HOSHINO`}
        description={work.description ? work.description.slice(0, 150) : `映像作家 EIJI HOSHINOの作品「${work.title}」(${work.year})。${work.category}ジャンルの映像作品です。`}
        keywords={`${work.title},${work.category},EIJI HOSHINO,New Age Cinema,映像作品`}
        canonical={`/works/${work.id}`}
        jsonLd={jsonLd}
      />
      <Header />

      <main className="pt-24 md:pt-32 px-5 md:px-12 pb-20 md:pb-32">
        {/* Video Section */}
        <div className="max-w-6xl mx-auto mb-12 md:mb-24">
          <div className="border-t border-[#00F0FF] pt-1">
            <div className="aspect-video bg-black">
              <iframe
                src={work.videoUrl}
                title={work.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>

        {/* Work Info Section */}
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-10 md:gap-24">
            {/* Left: Work Info */}
            <div className="w-full md:w-3/5">
              <h1 className="text-3xl md:text-5xl font-light text-black mb-3 md:mb-4">{work.title}</h1>
              <p className="text-sm font-mono text-gray-500 mb-8 md:mb-12">{work.year}</p>
              <p className="text-sm md:text-base font-light text-black leading-loose max-w-xl">
                {work.description}
              </p>
            </div>

            {/* Right: Credits */}
            <div className="w-full md:w-2/5">
              <div className="flex gap-4 md:gap-8">
                <h2 className="text-xs font-light tracking-[0.2em] text-gray-400" style={{ writingMode: 'vertical-rl' }}>
                  CREDITS
                </h2>
                <div className="flex-1 space-y-5 md:space-y-6">
                  {Object.entries(work.credits).map(([role, name]) => (
                    <div key={role}>
                      <p className="text-xs font-light tracking-[0.1em] text-[#00F0FF] mb-1 uppercase">
                        {role.replace(/([A-Z])/g, ' $1').trim()}
                      </p>
                      <p className="text-sm font-light text-black">{name}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Section */}
        <div className="max-w-6xl mx-auto mt-16 md:mt-32">
          <div className="flex items-center justify-between gap-4">
            {prevWork ? (
              <Link
                to={`/works/${prevWork.id}`}
                className="border border-[#00F0FF] px-4 md:px-8 py-3 md:py-4 text-xs md:text-sm font-light tracking-[0.1em] md:tracking-[0.2em] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-white transition-all duration-300 whitespace-nowrap"
              >
                ← PREV
              </Link>
            ) : (
              <div></div>
            )}

            <Link
              to="/works"
              className="text-xs md:text-sm font-light tracking-[0.1em] md:tracking-[0.2em] text-gray-500 hover:text-[#00F0FF] transition-colors duration-300 whitespace-nowrap"
            >
              BACK TO WORKS
            </Link>

            {nextWork ? (
              <Link
                to={`/works/${nextWork.id}`}
                className="border border-[#00F0FF] px-4 md:px-8 py-3 md:py-4 text-xs md:text-sm font-light tracking-[0.1em] md:tracking-[0.2em] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-white transition-all duration-300 whitespace-nowrap"
              >
                NEXT →
              </Link>
            ) : (
              <div></div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}